/* Admin · Order 360 drawer
 *
 * Surfaces every operational state on an order — shipping, prebook,
 * cancellation, QI claim, tracking, invoice, related parties, and a
 * unified reverse-chrono event timeline.
 *
 * Data source: GET /api/admin/orders/{oid}/order-360 (hydrated).
 */
import { useEffect, useState, useMemo } from "react";
import api from "../lib/api";
import { Sheet, SheetContent, SheetCloseX, SheetHeader, SheetTitle } from "./ui/sheet";
import { toast } from "sonner";
import {
  Loader2, Package, Truck, AlertTriangle, XCircle, FileText, Clock,
  ShieldCheck, MapPin, Sparkles, ExternalLink, CheckCircle2, ScrollText,
  Download, ReceiptText, DollarSign, User as UserIcon, Building2,
  CalendarClock, Tag, Hash, RefreshCw, Wrench,
} from "lucide-react";
import { AdminOrderControls } from "./AdminControlsPanel";
import AdminClaimReply from "./AdminClaimReply";

function money(n) {
  if (n == null) return "—";
  // Defensive: some legacy claim docs persist offer as {amount, kind}.
  if (typeof n === "object") n = n.amount ?? n.value ?? null;
  if (n == null || !Number.isFinite(Number(n))) return "—";
  return `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function fmtDate(iso, withTime = true) {
  if (!iso) return "—";
  try {
    return withTime ? new Date(iso).toLocaleString() : new Date(iso).toLocaleDateString();
  } catch { return iso; }
}

function KvRow({ label, value, mono = false, testid }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="flex items-baseline gap-3 py-1.5 border-b border-border/40 last:border-b-0" data-testid={testid}>
      <div className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/50 w-36 shrink-0">{label}</div>
      <div className={`text-[13px] ${mono ? "mono" : ""} text-foreground/85 break-words flex-1`}>{value}</div>
    </div>
  );
}

function Chip({ tone = "neutral", children }) {
  const tones = {
    neutral: "bg-secondary text-foreground/70 border-border",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber:   "bg-amber-50 text-amber-800 border-amber-200",
    rose:    "bg-rose-50 text-rose-700 border-rose-200",
    indigo:  "bg-indigo-50 text-indigo-700 border-indigo-200",
  };
  return <span className={`inline-flex items-center gap-1 chip border text-[10.5px] ${tones[tone]}`}>{children}</span>;
}

function Empty({ icon: Icon, label }) {
  return (
    <div className="py-8 text-center text-[12.5px] text-foreground/50 flex flex-col items-center gap-1.5">
      <Icon className="w-5 h-5 opacity-40"/>
      {label}
    </div>
  );
}

function SectionTitle({ icon: Icon, title, hint }) {
  return (
    <div className="mb-3">
      <div className="flex items-center gap-2">
        {Icon && <Icon className="w-3.5 h-3.5 text-foreground/55"/>}
        <h3 className="text-[12.5px] font-semibold tracking-tight">{title}</h3>
      </div>
      {hint && <div className="text-[11.5px] text-foreground/55 mt-0.5">{hint}</div>}
    </div>
  );
}

// ── Status visualizer ────────────────────────────────────────────────────
const STATUS_TONES = {
  pending:           "amber",
  prebook_offered:   "indigo",
  prebook_active:    "indigo",
  processing:        "indigo",
  ready_for_pickup:  "indigo",
  transit:           "indigo",
  delivered:         "emerald",
  paid:              "emerald",
  cancelled:         "rose",
};

// ── Tabs ─────────────────────────────────────────────────────────────────
function OverviewTab({ data }) {
  const o = data.order;
  const inv = data.invoice;
  const buyer = data.buyer || {};
  const seller = data.seller || {};
  const t = data.seller_tenant || {};
  const settings = t.settings || {};
  const policy = settings.profile || {};
  return (
    <div className="space-y-4">
      {/* Big header */}
      <div className="rounded-xl bg-gradient-to-br from-foreground/[0.03] to-secondary border border-border p-4" data-testid="order-360-overview">
        <div className="flex items-start justify-between mb-3 gap-2 flex-wrap">
          <div>
            <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/50">Order code</div>
            <div className="display text-2xl tracking-tight mt-0.5">{o.code}</div>
          </div>
          <Chip tone={STATUS_TONES[o.status] || "neutral"}>{(o.status || "").replace(/_/g, " ")}</Chip>
        </div>
        <KvRow label="Buyer PO #" value={o.po_number || "—"} mono/>
        <KvRow label="Order source" value={o.source ? o.source.replace(/_/g, " ") : "—"}/>
        <KvRow label="Created by" value={o.created_by || (o.source === "catalog" ? "Buyer (self-serve catalog)" : "—")}/>
        <KvRow label="Placed" value={fmtDate(o.created_at)}/>
        <KvRow label="Total" value={money(o.total)}/>
        <KvRow label="Items" value={o.items?.length ? `${o.items.length} line${o.items.length === 1 ? "" : "s"}` : "—"}/>
        <KvRow label="Terms" value={(o.terms || "").toUpperCase()}/>
        {o.expected_ready_at && <KvRow label="Expected ready" value={fmtDate(o.expected_ready_at, false)}/>}
        {o.actual_ready_at   && <KvRow label="Actual ready"   value={fmtDate(o.actual_ready_at)}/>}
        {o.delivered_at      && <KvRow label="Delivered"      value={fmtDate(o.delivered_at)}/>}
        {o.cancelled_at      && <KvRow label="Cancelled"      value={fmtDate(o.cancelled_at)}/>}
        {o.seller_note && <KvRow label="Seller note" value={o.seller_note}/>}
        {o.updated_at && <KvRow label="Last updated" value={fmtDate(o.updated_at)}/>}
      </div>

      {/* Parties */}
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-xl border border-border bg-white p-3.5" data-testid="order-360-buyer">
          <div className="flex items-center gap-1.5 mb-2 text-[11px] mono uppercase tracking-[0.16em] text-foreground/55">
            <UserIcon className="w-3 h-3"/>Buyer
          </div>
          <div className="text-[13px] font-medium">{buyer.company || buyer.name || o.buyer_name}</div>
          <div className="text-[11.5px] text-foreground/60 mono break-all">{buyer.email}</div>
          {buyer.phone && <div className="text-[11.5px] text-foreground/60 mono">{buyer.phone}</div>}
        </div>
        <div className="rounded-xl border border-border bg-white p-3.5" data-testid="order-360-seller">
          <div className="flex items-center gap-1.5 mb-2 text-[11px] mono uppercase tracking-[0.16em] text-foreground/55">
            <Building2 className="w-3 h-3"/>Seller
          </div>
          <div className="text-[13px] font-medium">{seller.company || seller.name || o.seller_name}</div>
          <div className="text-[11.5px] text-foreground/60 mono break-all">{seller.email}</div>
          {t.slug && <div className="text-[11px] text-foreground/45 mono">/s/{t.slug}</div>}
        </div>
      </div>

      {/* Seller policies applied at order time */}
      {(policy.restocking_fee_pct != null || settings.default_lead_days != null || settings.qi_window_days != null) && (
        <div className="rounded-xl border border-border bg-white p-3.5" data-testid="order-360-policies">
          <SectionTitle icon={ShieldCheck} title="Seller policies in effect"/>
          <KvRow label="Default lead days" value={settings.default_lead_days != null ? `${settings.default_lead_days} days` : "—"}/>
          <KvRow label="QI window" value={settings.qi_window_days != null ? `${settings.qi_window_days} days` : "—"}/>
          <KvRow label="Restocking fee" value={policy.restocking_fee_pct != null ? `${policy.restocking_fee_pct}%` : "—"}/>
          <KvRow label="Default terms" value={settings.default_terms}/>
        </div>
      )}

      {/* Reminders + counters */}
      {(o.payment_reminders_sent || o.pickup_reminders_sent || o.ready_date_set_at) && (
        <div className="rounded-xl border border-border bg-white p-3.5" data-testid="order-360-counters">
          <SectionTitle icon={Clock} title="System-side counters"/>
          {o.ready_date_set_at      && <KvRow label="Ready date stamped" value={fmtDate(o.ready_date_set_at)}/>}
          {o.payment_reminders_sent && <KvRow label="Payment reminders sent" value={o.payment_reminders_sent}/>}
          {o.pickup_reminders_sent  && <KvRow label="Pickup reminders sent"  value={o.pickup_reminders_sent}/>}
          {o.last_adjusted_at       && <KvRow label="Last admin adjustment"   value={fmtDate(o.last_adjusted_at)}/>}
        </div>
      )}

      {/* Replacement linkage */}
      {data.replacement_of && (
        <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-3.5" data-testid="order-360-replacement">
          <SectionTitle icon={RefreshCw} title="Replacement order"
            hint={`This order replaces ${data.replacement_of.code} (closed via QI claim).`}/>
          <KvRow label="Original" value={data.replacement_of.code} mono/>
          <KvRow label="Status" value={data.replacement_of.status}/>
          <KvRow label="Original total" value={money(data.replacement_of.total)}/>
          {data.replacement_of.claim_code && <KvRow label="Via claim" value={data.replacement_of.claim_code} mono/>}
        </div>
      )}

      {/* Linked invoice quick view */}
      {inv && (
        <div className="rounded-xl border border-border bg-white p-3.5" data-testid="order-360-invoice">
          <SectionTitle icon={ReceiptText} title="Linked invoice"/>
          <KvRow label="Invoice #" value={inv.code} mono/>
          <KvRow label="Amount" value={money(inv.amount)}/>
          <KvRow label="Status" value={inv.status}/>
          <KvRow label="Due" value={fmtDate(inv.due_date, false)}/>
          {inv.financed && <KvRow label="Financed" value="Yes (verqo factoring)"/>}
          {inv.insured  && <KvRow label="Insured"  value="Allianz Trade"/>}
          {inv.paid_at  && <KvRow label="Paid"     value={fmtDate(inv.paid_at)}/>}
        </div>
      )}
    </div>
  );
}

function ShippingTab({ data }) {
  const o = data.order;
  const si = o.shipping_info || {};
  const ship = data.shipment;
  const w = o.shipping_waiver;
  const hist = o.shipping_mode_history || [];
  return (
    <div className="space-y-4">
      {/* Mode + waiver */}
      <div className="rounded-xl border border-border bg-white p-3.5" data-testid="order-360-mode">
        <SectionTitle icon={Truck} title="Shipping mode"/>
        <KvRow label="Mode" value={o.shipping_mode ? o.shipping_mode.replace(/_/g, " ") : "—"}/>
        <KvRow label="Payer" value={o.shipping_payer}/>
        <KvRow label="Shipping fee" value={o.shipping_fee != null ? money(o.shipping_fee) : "—"}/>
        {w && (
          <>
            <KvRow label="Waiver accepted" value={w.accepted ? "Yes" : "No"}/>
            <KvRow label="Accepted at"     value={fmtDate(w.accepted_at)}/>
            <KvRow label="IP"              value={w.ip} mono/>
            <KvRow label="User agent"      value={w.user_agent ? `${w.user_agent.slice(0, 80)}${w.user_agent.length > 80 ? "…" : ""}` : "—"}/>
          </>
        )}
      </div>

      {/* Mode history */}
      {hist.length > 0 && (
        <div className="rounded-xl border border-border bg-white p-3.5" data-testid="order-360-mode-history">
          <SectionTitle icon={Clock} title="Mode change history"/>
          <ul className="divide-y divide-border/40">
            {hist.map((h, i) => (
              <li key={i} className="py-1.5 text-[12px] flex items-center gap-2">
                <Clock className="w-3 h-3 text-foreground/40"/>
                <span className="flex-1">{h.from || "—"} → <strong>{h.to}</strong> · by {h.by || "—"}</span>
                <span className="text-foreground/40 mono text-[10.5px]">{fmtDate(h.at).split(",")[0]}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Freight details */}
      <div className="rounded-xl border border-border bg-white p-3.5" data-testid="order-360-freight">
        <SectionTitle icon={Package} title="Freight details (seller-entered)"/>
        {Object.keys(si).length === 0 ? (
          <Empty icon={Package} label="No freight details entered yet."/>
        ) : (
          <>
            <KvRow label="Package count"  value={si.package_count}/>
            <KvRow label="Pallet count"   value={si.pallet_count}/>
            <KvRow label="Total weight"   value={si.total_weight_lb ? `${si.total_weight_lb} lb` : "—"}/>
            <KvRow label="Dimensions"     value={si.package_dimensions}/>
            <KvRow label="Pickup window"  value={si.pickup_window}/>
            <KvRow label="Pickup location" value={si.pickup_location}/>
            <KvRow label="Pickup notes"   value={si.pickup_notes}/>
            <KvRow label="BOL #"          value={si.bol_number} mono/>
            <KvRow label="Freight class"  value={si.freight_class}/>
            <KvRow label="Hazmat"         value={si.is_hazmat ? "Yes" : (si.is_hazmat === false ? "No" : "—")}/>
            <KvRow label="Liftgate at delivery" value={si.liftgate_at_delivery ? "Yes" : (si.liftgate_at_delivery === false ? "No" : "—")}/>
          </>
        )}
      </div>

      {/* Carrier / tracking */}
      {(ship || o.tracking) && (
        <div className="rounded-xl border border-border bg-white p-3.5" data-testid="order-360-tracking">
          <SectionTitle icon={MapPin} title="Carrier & tracking"/>
          {ship && (
            <>
              <KvRow label="Carrier" value={ship.carrier}/>
              <KvRow label="Service" value={ship.service}/>
              <KvRow label="Tracking #" value={ship.tracking || o.tracking} mono/>
              <KvRow label="Cost" value={ship.cost != null ? money(ship.cost) : "—"}/>
              <KvRow label="Created" value={fmtDate(ship.created_at)}/>
              {ship.label_url && (
                <KvRow label="Label" value={
                  <a href={ship.label_url} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1 underline text-foreground/75">
                    Open label <ExternalLink className="w-3 h-3"/>
                  </a>
                }/>
              )}
            </>
          )}
          {!ship && o.tracking && <KvRow label="Tracking #" value={o.tracking} mono/>}
          {o.est_delivery && <KvRow label="Estimated delivery" value={fmtDate(o.est_delivery, false)}/>}
        </div>
      )}

      {/* Carrier events */}
      {(data.shipment_events || []).length > 0 && (
        <div className="rounded-xl border border-border bg-white p-3.5" data-testid="order-360-carrier-events">
          <SectionTitle icon={Clock} title="Carrier webhook events"
            hint={`${data.shipment_events.length} events (most recent first)`}/>
          <ul className="divide-y divide-border/40">
            {data.shipment_events.map((e, i) => (
              <li key={e.id || i} className="py-1.5 text-[12px]">
                <div className="flex items-baseline gap-2">
                  <Clock className="w-3 h-3 text-foreground/40 mt-0.5 shrink-0"/>
                  <div className="flex-1">
                    <div className="font-medium">{e.status || e.kind || "tracking update"}</div>
                    {e.message && <div className="text-foreground/60 mt-0.5">{e.message}</div>}
                    {e.location && <div className="text-foreground/45 text-[11px] mt-0.5">{e.location}</div>}
                  </div>
                  <span className="text-foreground/40 mono text-[10.5px]">{fmtDate(e.occurred_at || e.created_at).split(",")[0]}</span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function PrebookTab({ data }) {
  const pb = data.order.prebook;
  if (!pb) return <Empty icon={Sparkles} label="This order does not use the Prebook flow."/>;
  const exts = pb.extensions || [];
  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-indigo-200 bg-indigo-50/40 p-3.5" data-testid="order-360-prebook-summary">
        <SectionTitle icon={Sparkles} title="Prebook summary"/>
        <KvRow label="Status" value={pb.status}/>
        <KvRow label="Deposit %" value={pb.deposit_pct != null ? `${pb.deposit_pct}%` : "—"}/>
        <KvRow label="Deposit amount" value={money(pb.deposit_amount)}/>
        <KvRow label="Balance amount" value={money(pb.balance_amount)}/>
        <KvRow label="Promised ready by" value={fmtDate(pb.promised_ready_by, false)}/>
        <KvRow label="Original promise"  value={fmtDate(pb.original_promised_ready_by, false)}/>
        <KvRow label="Offered" value={fmtDate(pb.offered_at)}/>
        <KvRow label="Accepted" value={fmtDate(pb.accepted_at)}/>
        <KvRow label="Declined" value={fmtDate(pb.declined_at)}/>
        <KvRow label="Cancelled" value={fmtDate(pb.cancelled_at)}/>
        <KvRow label="Refund status" value={pb.refund_status}/>
        <KvRow label="Refund amount"  value={money(pb.refund_amount)}/>
      </div>

      {exts.length > 0 && (
        <div className="rounded-xl border border-border bg-white p-3.5" data-testid="order-360-prebook-ext">
          <SectionTitle icon={CalendarClock} title="Extension requests"
            hint={`${exts.length} of ${pb.max_extensions || 3} allowed`}/>
          <ul className="divide-y divide-border/40">
            {exts.map((ex, i) => (
              <li key={ex.id || i} className="py-2 text-[12.5px]">
                <div className="flex items-center gap-2 mb-1">
                  <Chip tone={ex.status === "accepted" || ex.status === "auto_accepted" ? "emerald" : ex.status === "rejected" ? "rose" : "amber"}>
                    {ex.status || "pending"}
                  </Chip>
                  <span className="text-foreground/60 text-[11px]">{fmtDate(ex.requested_at).split(",")[0]}</span>
                </div>
                <div className="text-foreground/85">→ {fmtDate(ex.new_promised_ready_by, false)}</div>
                {ex.reason && <div className="text-foreground/60 text-[11.5px] mt-0.5">{ex.reason}</div>}
                {ex.decided_at && <div className="text-foreground/45 text-[11px] mt-0.5">Decided {fmtDate(ex.decided_at)}</div>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function CancellationTab({ data }) {
  const o = data.order;
  const c = o.cancellation;
  if (!c && !o.cancelled_at) return <Empty icon={CheckCircle2} label="Order is not cancelled."/>;
  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-rose-200 bg-rose-50/40 p-3.5" data-testid="order-360-cancel-summary">
        <SectionTitle icon={XCircle} title="Cancellation"/>
        <KvRow label="Cancel reason" value={o.cancel_reason}/>
        <KvRow label="Cancelled at"  value={fmtDate(o.cancelled_at)}/>
        {c && (
          <>
            <KvRow label="Type"              value={c.type}/>
            <KvRow label="Cancelled by"      value={c.cancelled_by_role || c.cancelled_by}/>
            <KvRow label="Restocking fee %" value={c.restocking_fee_pct != null ? `${c.restocking_fee_pct}%` : "—"}/>
            <KvRow label="Restocking $"      value={money(c.restocking_fee)}/>
            <KvRow label="Refund amount"     value={money(c.refund_amount)}/>
            <KvRow label="Refund status"     value={c.refund_status}/>
            <KvRow label="Override reason"   value={c.restocking_override_reason}/>
            {c.override_by && <KvRow label="Override by" value={c.override_by}/>}
            {o.last_adjusted_at && <KvRow label="Last adjusted" value={fmtDate(o.last_adjusted_at)}/>}
          </>
        )}
      </div>

      {(data.wallet_txns || []).length > 0 && (
        <div className="rounded-xl border border-border bg-white p-3.5" data-testid="order-360-wallet-txns">
          <SectionTitle icon={DollarSign} title="Related wallet movements"
            hint={`${data.wallet_txns.length} txns linked to ${data.invoice?.code || "this order"}`}/>
          <ul className="divide-y divide-border/40">
            {data.wallet_txns.map(t => (
              <li key={t.id} className="py-1.5 text-[12px] flex items-center gap-2">
                <span className="text-foreground/40 text-[10.5px] mono">{fmtDate(t.created_at).split(",")[0]}</span>
                <span className="flex-1">
                  <span className="font-medium">{t.type}</span>
                  <span className="text-foreground/55"> · {t.method}</span>
                </span>
                <span className={`mono ${(t.amount || 0) > 0 ? "text-emerald-700" : "text-rose-700"}`}>
                  {(t.amount || 0) > 0 ? "+" : ""}{money(t.amount)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function ClaimTab({ data, onChange }) {
  const c = data.claim;
  if (!c) return <Empty icon={ShieldCheck} label="No QI claim filed on this order."/>;
  const events = c.events || [];
  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-3.5" data-testid="order-360-claim-summary">
        <SectionTitle icon={AlertTriangle} title="QI claim"/>
        <KvRow label="Claim #"   value={c.code} mono/>
        <KvRow label="Reason"    value={c.reason}/>
        <KvRow label="Requested outcome" value={c.requested_outcome}/>
        <KvRow label="Requested $"       value={money(c.requested_amount)}/>
        <KvRow label="Current offer"     value={money(c.current_offer)}/>
        <KvRow label="Resolution"        value={c.resolution}/>
        <KvRow label="Status"            value={c.status}/>
        <KvRow label="Opened"            value={fmtDate(c.created_at)}/>
        <KvRow label="Deadline"          value={fmtDate(c.deadline_at)}/>
        <KvRow label="QI window"         value={c.qi_window_days != null ? `${c.qi_window_days} days` : "—"}/>
        <KvRow label="Score impact"      value={c.score_impact != null ? c.score_impact : "—"}/>
        <KvRow label="Allianz claim ref" value={c.allianz_claim_ref} mono/>
        {c.admin_intervened && <KvRow label="Admin intervened" value="Yes · audit-logged"/>}
      </div>

      {events.length > 0 && (
        <div className="rounded-xl border border-border bg-white p-3.5" data-testid="order-360-claim-events">
          <SectionTitle icon={ScrollText} title="Claim event log" hint={`${events.length} events`}/>
          <ul className="divide-y divide-border/40">
            {events.map((e, i) => (
              <li key={e.id || i} className="py-2 text-[12px]">
                <div className="flex items-baseline gap-2">
                  <span className="text-foreground/40 text-[10.5px] mono shrink-0">{fmtDate(e.at).split(",")[0]}</span>
                  <div className="flex-1">
                    <div className="font-medium flex items-center gap-2">
                      {e.label || e.status || e.kind || "event"}
                      {e.sender_kind === "admin_mediation" && (
                        <span className="text-[9.5px] mono uppercase tracking-[0.18em] text-indigo-700 px-1.5 py-0.5 bg-indigo-50 border border-indigo-200 rounded">
                          admin mediation
                        </span>
                      )}
                    </div>
                    {e.text && <div className="text-foreground/75 text-[12px] mt-0.5">{e.text}</div>}
                    {e.note && <div className="text-foreground/60 text-[11.5px] mt-0.5">{e.note}</div>}
                    {e.by && <div className="text-foreground/45 text-[11px] mt-0.5">by {e.by}</div>}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <AdminClaimReply claim={c} onChange={() => onChange?.()}/>
    </div>
  );
}

function ItemsTab({ data }) {
  const items = data.order.items || [];
  if (items.length === 0) return <Empty icon={Package} label="No line items on this order."/>;
  const total = items.reduce((s, it) => s + ((it.qty || 0) * (it.price || it.unit_price || 0)), 0);
  return (
    <div className="space-y-3">
      <SectionTitle icon={Package} title="Line items" hint={`${items.length} line${items.length === 1 ? "" : "s"}`}/>
      <div className="rounded-xl border border-border bg-white overflow-hidden" data-testid="order-360-items">
        <table className="w-full text-[12.5px]">
          <thead className="bg-secondary/50 text-[10px] mono uppercase tracking-[0.18em] text-foreground/55">
            <tr>
              <th className="text-left px-3 py-2 font-normal">SKU</th>
              <th className="text-left px-3 py-2 font-normal">Item</th>
              <th className="text-right px-3 py-2 font-normal">Qty</th>
              <th className="text-right px-3 py-2 font-normal">Unit</th>
              <th className="text-right px-3 py-2 font-normal">Subtotal</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {items.map((it, i) => {
              const qty = it.qty || 0;
              const price = it.price || it.unit_price || 0;
              return (
                <tr key={i}>
                  <td className="px-3 py-2 mono text-foreground/55">{it.sku || "—"}</td>
                  <td className="px-3 py-2">{it.name || it.title || it.product_name || "—"}</td>
                  <td className="px-3 py-2 text-right mono">{qty}</td>
                  <td className="px-3 py-2 text-right mono">{money(price)}</td>
                  <td className="px-3 py-2 text-right mono font-medium">{money(qty * price)}</td>
                </tr>
              );
            })}
            <tr className="bg-secondary/30">
              <td colSpan={4} className="px-3 py-2 text-right text-[11px] mono uppercase tracking-[0.16em] text-foreground/55">Calculated total</td>
              <td className="px-3 py-2 text-right mono font-semibold">{money(total)}</td>
            </tr>
            {data.order.total != null && Math.abs(total - data.order.total) > 0.01 && (
              <tr className="bg-amber-50/40">
                <td colSpan={4} className="px-3 py-2 text-right text-[11px] mono uppercase tracking-[0.16em] text-amber-700">On-doc total</td>
                <td className="px-3 py-2 text-right mono font-semibold text-amber-700">{money(data.order.total)}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DocumentsTab({ data }) {
  const o = data.order;
  const inv = data.invoice;
  const apiBase = (api.defaults.baseURL || "").replace(/\/$/, "");
  const download = async (path, filename) => {
    try {
      const r = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${filename}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Download failed");
    }
  };
  const buttons = [
    {
      id: "po",
      label: "Purchase order PDF",
      hint: `PO-${o.code}.pdf — buyer + seller header, line items, terms`,
      download: () => download(`/orders/${o.id}/po.pdf`, `PO-${o.code}.pdf`),
    },
    {
      id: "pack",
      label: "Pack list PDF",
      hint: "Used by warehouse + carrier handoff",
      download: () => download(`/orders/${o.id}/pack-list/pdf`, `PackList-${o.code}.pdf`),
    },
  ];
  if (inv) {
    buttons.push({
      id: "invoice-pdf",
      label: `Invoice PDF (${inv.code})`,
      hint: `${money(inv.amount)} · ${inv.status}`,
      download: () => download(`/invoices/${inv.id}/pdf`, `${inv.code}.pdf`),
    });
    buttons.push({
      id: "invoice-csv",
      label: `Invoice CSV (${inv.code})`,
      hint: "Accountant-ready import format",
      download: () => download(`/invoices/${inv.id}/csv`, `${inv.code}.csv`),
    });
  }
  return (
    <div className="space-y-3" data-testid="order-360-docs">
      <SectionTitle icon={FileText} title="Order documents"
        hint="One-click downloads for compliance / AP reconciliation."/>
      <ul className="space-y-2">
        {buttons.map(b => (
          <li key={b.id} className="rounded-xl border border-border bg-white p-3.5 flex items-center gap-3" data-testid={`order-360-doc-${b.id}`}>
            <FileText className="w-4 h-4 text-foreground/55 shrink-0"/>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-medium">{b.label}</div>
              <div className="text-[11.5px] text-foreground/55">{b.hint}</div>
            </div>
            <button onClick={b.download}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-foreground text-background text-[11.5px] hover:opacity-90"
              data-testid={`order-360-download-${b.id}`}>
              <Download className="w-3.5 h-3.5"/>Download
            </button>
          </li>
        ))}
      </ul>
      <p className="text-[11px] text-foreground/45 mt-2">
        Downloads stream through `{apiBase}` with your admin session cookie / bearer token.
      </p>
    </div>
  );
}

function TimelineTab({ data }) {
  const tl = data.timeline || [];
  if (tl.length === 0) return <Empty icon={ScrollText} label="No timeline events captured yet."/>;
  const toneCls = {
    neutral: "bg-secondary text-foreground/65",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber:   "bg-amber-50 text-amber-800 border-amber-200",
    rose:    "bg-rose-50 text-rose-700 border-rose-200",
    indigo:  "bg-indigo-50 text-indigo-700 border-indigo-200",
  };
  return (
    <div className="space-y-3" data-testid="order-360-timeline">
      <SectionTitle icon={ScrollText} title="Unified timeline" hint={`${tl.length} events · newest first`}/>
      <ol className="border-l-2 border-border/40 ml-1 space-y-3">
        {tl.map((e, i) => (
          <li key={i} className="relative pl-4" data-testid={`order-360-tl-${i}`}>
            <div className={`absolute -left-[7px] top-1 w-3 h-3 rounded-full border border-border ${toneCls[e.tone] || toneCls.neutral}`}/>
            <div className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/45">{fmtDate(e.at)}</div>
            <div className="text-[13px] mt-0.5 font-medium">{e.label}</div>
            <div className="text-[10.5px] mono text-foreground/45 mt-0.5">{e.kind}</div>
          </li>
        ))}
      </ol>
    </div>
  );
}

// ── Drawer shell ────────────────────────────────────────────────────────
const TABS = [
  { id: "overview",     label: "Overview",   icon: Tag },
  { id: "items",        label: "Line items", icon: Package },
  { id: "shipping",     label: "Shipping",   icon: Truck },
  { id: "prebook",      label: "Prebook",    icon: Sparkles },
  { id: "cancellation", label: "Cancellation", icon: XCircle },
  { id: "claim",        label: "QI Claim",   icon: AlertTriangle },
  { id: "documents",    label: "Documents",  icon: FileText },
  { id: "timeline",     label: "Timeline",   icon: ScrollText },
  { id: "controls",     label: "Admin Controls", icon: Wrench },
];

export default function AdminOrder360Drawer({ order, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    if (!order) return;
    setData(null); setLoading(true); setTab("overview");
    api.get(`/admin/orders/${order.id}/order-360`)
      .then(r => setData(r.data))
      .catch(e => toast.error(e.response?.data?.detail || "Failed to load order"))
      .finally(() => setLoading(false));
  }, [order?.id]);

  // Auto-grey tabs that have no data. Reduces noise for orders without
  // a prebook / cancellation / claim.
  const tabState = useMemo(() => {
    if (!data) return {};
    const o = data.order || {};
    return {
      prebook:      !!o.prebook,
      cancellation: !!(o.cancellation || o.cancelled_at),
      claim:        !!data.claim,
    };
  }, [data]);

  if (!order) return null;

  return (
    <Sheet open={!!order} onOpenChange={open => !open && onClose()}>
      <SheetContent className="w-full sm:max-w-2xl overflow-hidden p-0 flex flex-col" data-testid="order-360-drawer">
        <SheetCloseX/>
        <SheetHeader className="px-6 pt-6 pb-3 border-b border-border">
          <SheetTitle className="display text-2xl tracking-tight flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-accent"/>
            {order.code}
          </SheetTitle>
          <div className="text-[11.5px] text-foreground/55 mono">
            {order.buyer_name} → {order.seller_name} · {(order.status || "").replace(/_/g, " ")} · {money(order.total)}
          </div>
        </SheetHeader>

        {/* Tabs */}
        <div className="flex gap-1 px-4 pt-3 pb-2 border-b border-border overflow-x-auto" data-testid="order-360-tabs">
          {TABS.map(t => {
            const Icon = t.icon;
            const active = tab === t.id;
            const dim = (t.id === "prebook" && !tabState.prebook) ||
                        (t.id === "cancellation" && !tabState.cancellation) ||
                        (t.id === "claim" && !tabState.claim);
            return (
              <button key={t.id} onClick={() => setTab(t.id)}
                data-testid={`order-360-tab-${t.id}`}
                className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11.5px] border transition-all ${
                  active
                    ? "bg-foreground text-background border-foreground"
                    : `bg-white border-border ${dim ? "text-foreground/35" : "text-foreground/65 hover:border-foreground/40"}`
                }`}>
                <Icon className="w-3 h-3"/>{t.label}
                {dim && <span className="text-[9px] mono uppercase tracking-wider text-foreground/30 ml-0.5">·</span>}
              </button>
            );
          })}
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {loading || !data ? (
            <div className="flex items-center justify-center py-16 text-foreground/55">
              <Loader2 className="w-4 h-4 animate-spin mr-2"/>Loading order…
            </div>
          ) : (
            <>
              {tab === "overview"     && <OverviewTab data={data}/>}
              {tab === "items"        && <ItemsTab data={data}/>}
              {tab === "shipping"     && <ShippingTab data={data}/>}
              {tab === "prebook"      && <PrebookTab data={data}/>}
              {tab === "cancellation" && <CancellationTab data={data}/>}
              {tab === "claim"        && <ClaimTab data={data} onChange={() => {
                api.get(`/admin/orders/${order.id}/order-360`)
                  .then(r => setData(r.data))
                  .catch(() => {});
              }}/>}
              {tab === "documents"    && <DocumentsTab data={data}/>}
              {tab === "timeline"     && <TimelineTab data={data}/>}
              {tab === "controls"     && (
                <AdminOrderControls order={data.order} onChange={() => {
                  // Refresh after any override
                  api.get(`/admin/orders/${order.id}/order-360`)
                    .then(r => setData(r.data))
                    .catch(() => {});
                }}/>
              )}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
